import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { StorageService } from '../services/storage.service';
import { Usuario } from '../models/usuario';
import { JwtService } from '../services/jwt.service';
import { AuthService } from '../../auth/services/auth.service';
import { AlertaService } from '../services/alerta.service';
import { Router } from '@angular/router';

let refreshTokenRequest$: Observable<string> | null = null;

export const AuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {

  const storageService = inject(StorageService);
  const jwtService = inject(JwtService);
  const authService = inject(AuthService);
  const alertService = inject(AlertaService);
  const router = inject(Router)

  if (req.url.includes("/auth/renovarToken")) {
    return next(req);
  }

  let token = getToken(storageService);

  if (token === "") {
    return next(req);
  }

  if (jwtService.isExpired(token)) {
    alertService.openAlertaConfirmacion(
      "Seguridad",
      "Su sesión ha caducado",
      "warn",
      () => {
        storageService.storageClear();
        router.navigate(['/auth/login']);
      }
    );
    return throwError(() => new Error('SESSION_EXPIRED'));
  }

  if (jwtService.willExpireSoon(token)) {
    return getRefreshTokenRequest(
      authService,
      storageService,
      alertService,
      router
    ).pipe(
      switchMap((newToken) => {
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${newToken}`
          }
        });

        return next(authReq);
      })
    );
  }

  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  return next(authReq);

};

function getToken(storageService: StorageService): string {
  const user: Usuario | null | undefined = storageService.storageGet('usuario');
  return user?.token ?? '';
}

function getRefreshTokenRequest(
  authService: AuthService,
  storageService: StorageService,
  alertService: AlertaService,
  router: Router
): Observable<string> {
  if (refreshTokenRequest$) {
    return refreshTokenRequest$;
  }

  refreshTokenRequest$ = authService.refreshToken().pipe(
    map((respuesta: any) => {
      const newToken =
        respuesta?.value?.token || respuesta?.token || getToken(storageService);

      if (!newToken) {
        throw new Error('No se pudo renovar la sesión.');
      }

      return newToken;
    }),
    catchError((err) => {
      const mensaje = err?.message || 'No se pudo renovar la sesión.';
      alertService.openAlerta('ERROR', mensaje, 'error');
      storageService.storageClear();
      router.navigate(['/auth/login']);
      return throwError(() => err);
    }),
    finalize(() => {
      refreshTokenRequest$ = null;
    }),
    shareReplay(1)
  );

  return refreshTokenRequest$;
}
